import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  InfoBox,
} from '@biznomi/ui-react'
import {
  bulkCreateEmployees,
  downloadEmployeeTemplate,
  type BulkResult,
} from '@/lib/api/admin'
import { useInitialPasswordRule, initialPasswordHintMany } from '@/lib/initial-password'

/**
 * 엑셀로 여러 명 한번에 추가 — ADMIN 전용.
 *
 * ★ 쓰는 사람은 사장이지 담당자가 아니다.
 *   "헤더" "행" "일괄" 같은 말은 여기서 안 쓴다. 대신 **양식을 내려받아 채우는**
 *   순서로 만든다 — 컬럼 이름을 글로 설명하면 오타 한 번에 파일 전체가 실패하는데,
 *   빈 양식을 주면 그 실패 자체가 없어진다.
 *
 * ★ 결과를 반드시 끝까지 보여준다.
 *   과거 대량 등록에서 사람이 **조용히 누락**된 적이 있다(커넥션풀 고갈 + 부서 동시 생성,
 *   6/23 e6fdddf 수정). "127명 올렸는데 119명만 들어갔다"를 그 자리에서 알아야 한다.
 */

const MAX_MB = 5

/**
 * 서버 오류 문구를 사장이 읽을 말로 바꾼다.
 *
 * ⚠️ 서버가 오류 코드를 주지 않아 문구로 맞춘다. 서버 문구가 바뀌면 여기 매칭이
 *    빗나가는데, 그때는 서버 문구를 그대로 보여준다(fallback). 틀린 말이 뜨는 게
 *    아니라 딱딱한 말이 뜰 뿐이다.
 *    DCMC(Svelte) 도 같은 서버 문구를 쓰므로 서버를 고치지 않고 여기서만 바꾼다.
 */
const PLAIN_ERROR: Record<string, string> = {
  '이름 필수': '이름 칸이 비어 있어요',
  '이메일 필수': '이메일 칸이 비어 있어요',
  '이메일 형식 오류': '이메일 주소 모양이 아니에요',
  '이메일 중복': '이미 등록된 이메일이에요',
  '사번 중복': '이미 등록된 사번이에요',
  '사번 필수 (사번 로그인 조직)': '사번 칸이 비어 있어요',
  '이메일 또는 사번 중복': '이미 등록된 사람이에요',
}

function plainError(msg?: string): string {
  if (!msg) return '확인이 필요해요'
  return PLAIN_ERROR[msg] ?? msg
}

/** 파일 자체가 거절될 때의 문구. 양식만 받고 아무것도 안 채우는 경우가 제일 흔하다 */
const PLAIN_FILE_ERROR: Record<string, string> = {
  '등록할 데이터가 없습니다.': '추가할 사람이 없어요. 양식에 이름을 채워서 다시 올려 주세요.',
  '데이터가 없습니다. 2행부터 데이터를 입력해주세요.':
    '파일이 비어 있어요. 양식을 받아 이름을 채운 다음 올려 주세요.',
  '이름 컬럼이 필수입니다. 헤더를 확인해주세요.':
    '맨 윗줄에 “이름” 칸이 없어요. 양식을 새로 받아서 쓰는 게 빠릅니다.',
  '엑셀 파일만 업로드 가능합니다 (.xlsx, .xls)': '엑셀 파일(.xlsx)만 올릴 수 있어요.',
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EmployeeBulkDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient()
  // 첫 비밀번호 규칙 — 개별 등록과 **같은 것**을 쓴다 (lib/initial-password.ts)
  const pwRule = useInitialPasswordRule()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<BulkResult | null>(null)
  const [error, setError] = useState('')
  const [gettingTemplate, setGettingTemplate] = useState(false)

  function reset() {
    setFile(null)
    setResult(null)
    setError('')
    if (inputRef.current) inputRef.current.value = ''
  }

  async function getTemplate() {
    setGettingTemplate(true)
    const err = await downloadEmployeeTemplate()
    setGettingTemplate(false)
    if (err) setError(err)
  }

  const upload = useMutation({
    mutationFn: () => bulkCreateEmployees(file!),
    onSuccess: (res) => {
      if (!res.success || !res.data) {
        const raw = res.error ?? ''
        setError(PLAIN_FILE_ERROR[raw] ?? raw ?? '파일을 읽지 못했어요. 양식을 받아 다시 만들어 주세요.')
        return
      }
      setError('')
      setResult(res.data)
      // 한 명이라도 들어갔으면 목록을 새로 읽는다
      if (res.data.success > 0) qc.invalidateQueries({ queryKey: ['admin', 'employees'] })
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto">
        <DialogTitle>엑셀로 여러 명 추가</DialogTitle>
        <DialogDescription>
          양식을 받아 이름을 채운 다음, 그 파일을 그대로 올리면 됩니다.
        </DialogDescription>

        {!result && (
          <div className="mt-4 space-y-4">
            {/* 1단계 — 양식 받기. 컬럼 이름을 설명하지 않고 채울 파일을 준다 */}
            <div>
              <p className="mb-1.5 text-sm font-bold">1. 양식 받기</p>
              <Button
                variant="secondary"
                className="w-full"
                loading={gettingTemplate}
                onClick={getTemplate}
              >
                양식 내려받기
              </Button>
              <p className="mt-1.5 text-xs text-slate-400">
                엑셀을 열어 한 줄에 한 명씩 적으세요. <b>※</b> 로 시작하는 줄은 설명이라 그냥 둬도
                등록되지 않습니다.
              </p>
            </div>

            {/* 2단계 — 올리기 */}
            <div>
              <p className="mb-1.5 text-sm font-bold">2. 채운 파일 올리기</p>
              {/*
               * ⚠️ 네이티브 file input 의 "Choose File / No file chosen" 은 브라우저
               *    UI 언어대로 나오고 CSS 로 못 바꾼다. 영문 브라우저를 쓰는 사장에게
               *    화면 한가운데 영어가 뜬다. 그래서 입력칸은 숨기고(접근성은 유지)
               *    우리가 한글 버튼과 파일 이름을 그린다.
               */}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                aria-label="채운 엑셀 파일"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null
                  if (f && f.size > MAX_MB * 1024 * 1024) {
                    setError(`파일이 너무 커요 (${MAX_MB}MB까지)`)
                    setFile(null)
                    return
                  }
                  setError('')
                  setFile(f)
                }}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={() => inputRef.current?.click()}>
                  파일 고르기
                </Button>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-500">
                  {file ? file.name : '고른 파일 없음'}
                </span>
              </div>
            </div>

            {/*
              🔴 여기가 「사번이 있으면 사번이 첫 비밀번호」로 **하드코딩**돼 있었다.
                 9/9 에 개별 등록만 고치고 이 사본을 빠뜨렸고, 9/12 리허설에서 터졌다 —
                 조직이 FALLBACK 인데 이 문구를 읽은 관리자가 직원에게 사번을 알려 주면
                 **직원이 로그인을 못 한다.** 규칙은 조직 설정이 정한다.
              ⚠ 못 읽었으면 아무 말도 안 한다. 틀린 안내보다 없는 안내가 낫다.
            */}
            {pwRule && <InfoBox tone="info">{initialPasswordHintMany(pwRule)}</InfoBox>}

            {error && <InfoBox tone="danger">{error}</InfoBox>}
          </div>
        )}

        {/*
         * 결과. 실패가 0명이어도 숫자를 보여준다 —
         * 몇 명 들어갔는지 확인하지 않으면 누락을 영영 모른다
         */}
        {result && (
          <div className="mt-4 space-y-3">
            <InfoBox tone={result.failed > 0 ? 'warning' : 'success'}>
              <b>{result.success.toLocaleString()}명</b>을 추가했습니다.
              {result.failed > 0 && (
                <> {result.failed.toLocaleString()}명은 추가하지 못했습니다.</>
              )}
            </InfoBox>

            {result.failed > 0 && (
              <div>
                <p className="mb-1.5 text-sm font-bold text-slate-500">추가하지 못한 사람</p>
                <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-card bg-card">
                  {result.errors.map((e) => (
                    <li key={e.row} className="flex items-start justify-between gap-3 px-3 py-2">
                      <span className="min-w-0 text-sm">
                        {/* 엑셀에서 찾아 고쳐야 하므로 줄 번호를 그대로 알려준다 */}
                        <span className="text-slate-400">{e.row}번째 줄</span>{' '}
                        {e.name || e.email || '(빈 줄)'}
                      </span>
                      <span className="shrink-0 text-xs text-[var(--color-danger)]">
                        {plainError(e.error)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-xs text-slate-400">
                  엑셀에서 이 줄만 고쳐서 다시 올리세요. 이미 들어간 사람은 두 번 등록되지 않습니다.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {result ? '닫기' : '취소'}
          </Button>
          {!result && (
            <Button onClick={() => upload.mutate()} loading={upload.isPending} disabled={!file}>
              올리기
            </Button>
          )}
          {result && result.failed > 0 && (
            <Button variant="secondary" onClick={reset}>
              고쳐서 다시 올리기
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
